import { z as z3, ZodError, ZodTypeAny } from 'zod/v3';
import * as z4 from 'zod/v4';
import { ValueOf } from './utilTypes';
import debug from './debug';

export type ZodFieldExample = {
  name: string;
  example: unknown;
};

export type ZodFieldDescription = {
  description: string;
  examples: Array<ZodFieldExample>;
};

export function createFieldDescriptionWithExamples(
  description: string,
  examples: Array<ZodFieldExample>
): string {
  return JSON.stringify({ description, examples });
}

export function getZodEnumValuesFromEnum<T extends Record<string, string>>(
  enumType: T
): [ValueOf<T>, ...Array<ValueOf<T>>] {
  const kindValues = Object.values(enumType) as Array<ValueOf<T>>;
  const categoryEnum: [ValueOf<T>, ...Array<ValueOf<T>>] = [
    kindValues[0],
    ...kindValues.slice(1),
  ];
  return categoryEnum;
}

export function messageFromZodParseError(
  error: z3.ZodError | z4.ZodError
): string {
  return error.issues
    .map((zodIssue) => {
      const { code, path, message } = zodIssue;
      const received = 'received' in zodIssue ? zodIssue.received : undefined;
      return `[Property ${path.join(
        '.'
      )}]:: code: ${code}, message: ${message}, received: ${received}`;
    })
    .join('; ');
}

/**
 * I'd like this to be recursive but it creates an "infinite instantiation error" if I make it call itself.
 * This is probably just as good for normal usage?
 */
export type UnwrapZodType<T extends z3.ZodTypeAny> =
  T extends z3.ZodOptional<any>
    ? T['_def']['innerType']
    : T extends z3.ZodNullable<any>
      ? T['_def']['innerType'] extends z3.ZodOptional<any>
        ? T['_def']['innerType']['_def']['innerType']
        : T['_def']['innerType']
      : T;

type NullishZodObject<Shape extends z3.ZodRawShape> = z3.ZodObject<{
  [Key in keyof Shape]: z3.ZodNullable<
    z3.ZodOptional<UnwrapZodType<Shape[Key]>>
  >;
}>;

/**
 *  Take a zod shape and return a zod object with all the properties
 *
 *  Ask Ben about his keyboard shortcuts: ಠ_ಠ
 *  */
export function makeShapeNullish<Shape extends z3.ZodRawShape>(
  shape: Shape
): NullishZodObject<Shape> {
  return z3.object(
    Object.fromEntries(
      Object.entries(shape).map(([key, zodType]) => [key, zodType.nullish()])
    )
  ) as unknown as NullishZodObject<Shape>;
}

// Formats a ZodError for display in admin UI.
export function formatZodError(err: ZodError | z4.ZodError): string {
  return err.issues
    .map(
      ({ code, path, message }) => `${path.join('.')} [${code}]: ${message}.`
    )
    .join(' ');
}

export function makeSafeParseOrNullParser<Z extends ZodTypeAny | z4.ZodType>(
  schema: Z
): (
  value: unknown
) => (Z extends ZodTypeAny ? z3.infer<Z> : z4.infer<Z>) | null {
  return function safeParser(
    value: unknown
  ): (Z extends ZodTypeAny ? z3.infer<Z> : z4.infer<Z>) | null {
    return safeParseOrNull(schema, value);
  };
}

export function safeParseOrNull<Z extends ZodTypeAny | z4.ZodType>(
  schema: Z,
  value: unknown
): (Z extends ZodTypeAny ? z3.infer<Z> : z4.infer<Z>) | null {
  const parseResult = schema.safeParse(value);
  if (parseResult.success) {
    return parseResult.data;
  } else {
    // To access this line, set $debug = true in your console
    debug(() => {
      console.info('[zod] Failed to safeParse value', {
        value,
        error: parseResult.error,
      });
    });
  }
  // TODO: should we capture a warning here on parse failures?
  return null;
}

/**
 * Extracts display names from a Zod object schema's field descriptions.
 * Returns a map of field names to their descriptions.
 *
 * @example
 * ```ts
 * const schema = z.object({
 *   userId: z.string().describe('User ID (external)'),
 *   email: z.string().describe('Email address'),
 * });
 *
 * const displayNames = getDisplayNamesFromSchema(schema);
 * // { userId: 'User ID (external)', email: 'Email address' }
 * ```
 */
export function getDisplayNamesFromSchema<T extends z4.ZodObject>(
  schema: T
): Partial<Record<keyof z4.infer<T>, string>> {
  return Object.entries(schema.shape).reduce(
    (acc, [fieldName, fieldSchema]) => {
      const zodSchema = fieldSchema as z4.ZodType;
      const description = zodSchema.meta()?.description;
      if (description) {
        acc[fieldName as keyof z4.infer<T>] = description;
      }
      return acc;
    },
    {} as Partial<Record<keyof z4.infer<T>, string>>
  );
}
